import { T } from 'src/shared/helpers';
import {
    AttributePreviewAction,
    AttributePreviewItems,
    AttributePreviewOption,
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeOptionNode,
    ComposerAttributePreviewType,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerExtensionFieldNode,
    ComposerLogicalOperator,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    ComposerPricingNode,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions,
    GetActionTypeFromAiType,
    GetActionTypeFromValue,
    GetActionValueFromType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAreaNode } from './../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getParentGuid, getParentIDNumeric, getParentType, guidIsEmpty, randomCode, tryParsePlatformValue } from './composerHelpers';

export class ComposerAttributeNodeAdapter {
    static cloneData<T>(data: T | null | undefined): T | undefined {
        return data == null ? undefined : JSON.parse(JSON.stringify(data));
    }

    static hydrate<T>(model: T, data: any): T {
        const snapshot = ComposerAttributeNodeAdapter.cloneData(data);
        if (snapshot) for (const [key, value] of Object.entries(snapshot)) (model as any).set(key, value);
        return model;
    }

    // Backbone collections de-duplicate models by their idAttribute. Giving every unsaved
    // entity the traditional -1 id therefore made later siblings replace earlier ones before
    // the mutation was even built. Derive a stable negative id from the entity GUID instead.
    static temporaryNumericID(guid: string): number {
        let hash = 2166136261;
        for (let index = 0; index < guid.length; index++) {
            hash ^= guid.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        return -((hash >>> 0) || 1);
    }

    static saveNumericID(serverID: number | null | undefined, guid: string): number {
        return serverID != null && serverID > 0 ? serverID : ComposerAttributeNodeAdapter.temporaryNumericID(guid);
    }

    static saveAttributes(attributes: ComposerAttributeNode[]): {
        attributes: MPlaza.Attributes;
        groups: MPlaza.AttributeGroups;
        steps: MPlaza.AttributeSteps;
    } {
        const resultAttributes = new MPlaza.Attributes();
        const resultGroups = new MPlaza.AttributeGroups();
        const resultSteps = new MPlaza.AttributeSteps();

        attributes.forEach((attribute) => {
            if (attribute.type === ComposerAttributeType.Attribute) {
                resultAttributes.push(ComposerAttributeNodeAdapter.fromNode(attribute));
            } else if (attribute.type === ComposerAttributeType.AttributeGroup) {
                resultGroups.push(ComposerAttributeNodeAdapter.fromGroupNode(attribute));
            } else if (attribute.type === ComposerAttributeType.AttributeStep) {
                resultSteps.push(ComposerAttributeNodeAdapter.fromStepNode(attribute));
            }
        });
        return { attributes: resultAttributes, groups: resultGroups, steps: resultSteps };
    }

    static fromJSON(json: any): MPlaza.Attribute {
        const attribute = ComposerAttributeNodeAdapter.createNew();
        if (!json) {
            return attribute;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            attribute.set('attributeGuid', parsedJSON.attributeGuid || MPlaza.generateUUID());
            attribute.set('attributeCode', parsedJSON.attributeCode || randomCode('attribute'));
            attribute.set('stepGuid', parsedJSON.stepGuid || Zakeke.getEmptyUUID());
            attribute.set('groupGuid', parsedJSON.groupGuid || Zakeke.getEmptyUUID());
            attribute.set('stepID', parsedJSON.stepID || -1);
            attribute.set('groupID', parsedJSON.groupID || -1);
            attribute.set('name', parsedJSON.name || '');
            attribute.set('description', parsedJSON.description || '');
            attribute.set('enabled', parsedJSON.enabled !== false);
            attribute.set('displayOrder', parsedJSON.displayOrder || 0);
            attribute.set('defaultValue', parsedJSON.defaultValue || '');
            attribute.set('options', parsedJSON.options || []);
            attribute.set('preActions', parsedJSON.preActions || []);
            attribute.set('valueActions', parsedJSON.valueActions || []);
            attribute.set('postActions', parsedJSON.postActions || []);
            attribute.set('pricings', parsedJSON.pricings || []);
            attribute.set('constraintExpression', parsedJSON.constraintExpression || null);
            attribute.set('meshSelector', parsedJSON.meshSelector || null);
            attribute.set('sceneCameraLocationID', parsedJSON.sceneCameraLocationID || null);
            attribute.set('hideOptionsLabel', !!parsedJSON.hideOptionsLabel);
        }
        return attribute;
    }

    static fromJSONOption(json: any): MPlaza.AttributeOption {
        const option = ComposerAttributeNodeAdapter.createNewOption();
        if (!json) {
            return option;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            option.set('optionGuid', parsedJSON.optionGuid || MPlaza.generateUUID());
            option.set('name', parsedJSON.name || '');
            option.set('displayOrder', parsedJSON.displayOrder || 0);
            option.set('optionCode', parsedJSON.optionCode || randomCode('option'));
            option.set('description', parsedJSON.description || '');
            option.set('enabled', parsedJSON.enabled !== false);
            option.set('imageUrl', parsedJSON.imageUrl || '');
            option.set('tempImageFileKey', parsedJSON.tempImageFileKey || '');
            option.set('tempImageFileObjectID', parsedJSON.tempImageFileObjectID ?? -1);
            option.set('imageFileObjectID', parsedJSON.imageFileObjectID ?? -1);
            option.set('constraintExpression', parsedJSON.constraintExpression || null);
            option.set('isDefault', !!parsedJSON.isDefault);
            option.set('actions', parsedJSON.actions || []);
        }
        return option;
    }

    static fromJSONAction(json: any): MPlaza.AttributeSceneAction {
        const action = ComposerAttributeNodeAdapter.createNewAction();
        if (!json) {
            return action;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            action.set('actionGuid', parsedJSON.actionGuid || MPlaza.generateUUID());
            action.set('name', parsedJSON.name || '');
            action.set('enabled', parsedJSON.enabled !== false);
            action.set('selector', parsedJSON.selector || '');
            action.set('value', parsedJSON.value || '');
        }
        return action;
    }

    static fromJSONStep(json: any): MPlaza.AttributeStep {
        const step = ComposerAttributeNodeAdapter.createNewStep();
        if (!json) {
            return step;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            step.set('stepGuid', parsedJSON.stepGuid || MPlaza.generateUUID());
            step.set('name', parsedJSON.name || '');
            step.set('enabled', parsedJSON.enabled !== false);
            step.set('displayOrder', parsedJSON.displayOrder || 0);
            step.set('groupGuid', parsedJSON.groupGuid || Zakeke.getEmptyUUID());
        }
        return step;
    }

    static fromJSONGroup(json: any): MPlaza.AttributeGroup {
        const group = ComposerAttributeNodeAdapter.createNewGroup();
        if (!json) {
            return group;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            group.set('groupGuid', parsedJSON.groupGuid || MPlaza.generateUUID());
            group.set('name', parsedJSON.name || '');
            group.set('enabled', parsedJSON.enabled !== false);
            group.set('displayOrder', parsedJSON.displayOrder || 0);
        }
        return group;
    }

    static createExpression(link: ComposerAttributeNodeLink): MPlaza.AttributeProposition {
        const expression = new MPlaza.AttributeProposition();
        expression.set('attributeGuid', link.attributeID || '');
        expression.set('op', link.operation as unknown as MPlaza.ComparisonOperator);
        expression.set('value', link.optionID || '');
        return expression;
    }

    static createConstraint(links: ComposerAttributeNodeLink[], operation: ComposerLogicalOperator): MPlaza.AttributeExpression {
        const expression = new MPlaza.AttributeExpression();
        const propositions = links.map((link) => {
            return ComposerAttributeNodeAdapter.createExpression(link);
        });
        expression.set('propositions', propositions);
        expression.set('op', operation as unknown as MPlaza.LogicalOperator);
        return expression;
    }

    static fromNode(node: ComposerAttributeNode | null): MPlaza.Attribute {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNew();
        }
        // Start from the complete model loaded from the API, then apply the fields edited by
        // this screen. Rebuilding a blank entity here used to silently discard fields that the
        // current editor does not expose (attribute actions/type/default and future API fields).
        const attribute = ComposerAttributeNodeAdapter.hydrate(new MPlaza.Attribute(), node.data);
        attribute.set('attributeGuid', node.id || MPlaza.generateUUID());
        attribute.set('attributeID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        attribute.set('attributeCode', node.code || randomCode('attribute'));
        attribute.set('name', node.name || '');
        attribute.set('description', node.description || '');
        attribute.set('enabled', node.enabled !== false);
        attribute.set('displayOrder', node.displayOrder || 0);
        attribute.set('stepGuid', node.parentType === ComposerAttributeType.AttributeStep ? node.parentID : Zakeke.getEmptyUUID());
        attribute.set('stepID', node.parentType === ComposerAttributeType.AttributeStep ? node.parentIDNumeric : -1);
        attribute.set('groupGuid', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentID : Zakeke.getEmptyUUID());
        attribute.set('groupID', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentIDNumeric : -1);
        const options = new MPlaza.AttributeOptions();
        node.options.forEach((option) => {
            const hasPricing = node.pricings.some((pricing) => pricing.selectedOptionGuid === option.id);
            options.push(ComposerAttributeNodeAdapter.fromOptionNode(option, hasPricing));
        });
        attribute.set('options', options);
        const originalPricings = attribute.get('pricings');
        const pricings = new MPlaza.AttributePricings();
        node.pricings.forEach((nodePricing, pricingIndex) => {
            const originalPricing = originalPricings?.find?.(
                (item: any) =>
                    (nodePricing.attributePricingID > 0 && (item.get?.('attributePricingID') ?? item.attributePricingID) === nodePricing.attributePricingID) ||
                    (!!nodePricing.selectedOptionGuid && (item.get?.('selectedOptionGuid') ?? item.selectedOptionGuid) === nodePricing.selectedOptionGuid)
            );
            const pricing = ComposerAttributeNodeAdapter.hydrate(
                new MPlaza.AttributePricing(),
                originalPricing?.toJSON?.() ?? originalPricing
            );
            pricing.set(
                'attributePricingID',
                ComposerAttributeNodeAdapter.saveNumericID(
                    nodePricing.attributePricingID,
                    `${node.id}:${nodePricing.selectedOptionGuid || nodePricing.selectedValue}:${pricingIndex}`
                )
            );
            pricing.set('selectedOptionID', nodePricing.selectedOptionID || null);
            pricing.set('selectedOptionGuid', nodePricing.selectedOptionGuid || null);
            pricing.set('selectedValue', nodePricing.selectedValue || null);
            pricing.set('price', nodePricing.price || 0);
            pricing.set('pricingID', nodePricing.pricingID || 0);
            if (nodePricing.name) (pricing as any).set('name', nodePricing.name);
            pricings.push(pricing);
        });
        attribute.set('pricings', pricings);
        attribute.set('optionPreviewShapeID', (node.previewType as unknown as MPlaza.AttributeOptionPreviewShape) || 1);
        (attribute as any).set(
            'constraintExpression',
            node.links.length > 0
                ? ComposerAttributeNodeAdapter.createConstraint(node.links, node.linkOperation ?? ComposerLogicalOperator.AND)
                : null
        );
        attribute.set('meshSelector', node.objects || null);
        attribute.set('sceneCameraLocationID', node.camera || null);
        attribute.set('hideOptionsLabel', node.hideOptionsLabel || false);
        return attribute;
    }

    static fromGroupNode(node: ComposerAttributeNode | null): MPlaza.AttributeGroup {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewGroup();
        }
        const group = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeGroup(), node.data);
        group.set('groupGuid', node.id || MPlaza.generateUUID());
        group.set('groupID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        group.set('name', node.name || '');
        group.set('enabled', node.enabled !== false);
        group.set('displayOrder', node.displayOrder || 0);
        group.set('imageUrl', node.img || '');
        group.set('sceneCameraLocationID', node.camera || null);
        group.set('meshSelector', node.objects || null);
        group.set('direction', node.direction ?? 0);
        group.set('attributesAlwaysOpened', node.attributesAlwaysOpened ?? false);
        return group;
    }

    static fromStepNode(node: ComposerAttributeNode | null): MPlaza.AttributeStep {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewStep();
        }
        const step = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeStep(), node.data);
        step.set('stepGuid', node.id || MPlaza.generateUUID());
        step.set('stepID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        step.set('name', node.name || '');
        step.set('enabled', node.enabled !== false);
        step.set('displayOrder', node.displayOrder || 0);
        step.set('groupGuid', node.parentID || Zakeke.getEmptyUUID());
        step.set('groupID', node.parentIDNumeric || -1);
        (step as any).set('cameraLocationID', node.camera || null);
        return step;
    }

    static fromOptionNode(node: ComposerAttributeOptionNode | null, preserveEmptyCode = false): MPlaza.AttributeOption {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewOption();
        }
        const option = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeOption(), node.data);
        option.set('optionGuid', node.id || MPlaza.generateUUID());
        option.set('optionID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        option.set('name', node.name || '');
        option.set('description', node.description || '');
        option.set('enabled', node.enabled !== false);
        option.set('displayOrder', node.displayOrder || 0);
        option.set('optionCode', preserveEmptyCode ? node.code : node.code || randomCode('option'));
        option.set('imageUrl', node.img || '');
        // Rebuilding an AttributeOption without these file references makes the API
        // interpret a normal save as an explicit image deletion. Keep the references
        // captured when the option was loaded, unless the image was actually removed.
        option.set('tempImageFileKey', node.img ? node.data?.tempImageFileKey || '' : '');
        option.set('tempImageFileObjectID', node.img ? (node.data?.tempImageFileObjectID ?? -1) : -1);
        option.set('imageFileObjectID', node.img ? (node.data?.imageFileObjectID ?? -1) : -1);
        option.set(
            'constraintExpression',
            node.links.length > 0
                ? ComposerAttributeNodeAdapter.createConstraint(node.links, node.linkOperation ?? ComposerLogicalOperator.AND)
                : null
        );
        option.set('isDefault', node.isDefault || false);
        const actions = new MPlaza.AttributeSceneActions();
        node.actions.forEach((action) => {
            actions.push(ComposerAttributeNodeAdapter.fromActionNode(action));
        });
        option.set('actions', actions);
        (option as any).set(
            'extensionFields',
            (node.extensionFields ?? []).map((ef) => ({
                extensionFieldID: ef.extensionFieldID,
                name: ef.name || '',
                label: ef.label || '',
                value: ef.value || '',
                dataTypeID: ef.dataTypeID || 0,
                operationTypeID: ef.operationTypeID || 0,
                formatString: ef.formatString || ''
            }))
        );
        return option;
    }

    static fromActionNode(node: ComposerOptionActionNode | null): MPlaza.AttributeSceneAction {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewAction();
        }
        const action = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeSceneAction(), node.data);
        action.set('actionGuid', node.id || MPlaza.generateUUID());
        (action as any).set(
            'actionID',
            ComposerAttributeNodeAdapter.saveNumericID(
                node.data?.actionGuid === node.id ? node.data?.actionID : null,
                node.id
            )
        );
        action.set('name', node.name || '');
        action.set('enabled', node.enabled !== false);
        action.set('actionTypeID', GetActionValueFromType(node?.type || ComposerOptionActionsType.SHOW_OBJECTS));
        action.set('sceneElementTypeID', node.sceneElementType ?? MPlaza.SceneElementType.Mesh);
        action.set('execOrder', node.displayOrder || 0);
        action.set('selector', node.selector || '');
        action.set('propertyName', node.property || '');
        action.set('value', node.value || '');
        return action;
    }

    static createNew(displayOrder?: number): MPlaza.Attribute {
        const attribute = new MPlaza.Attribute();
        attribute.set('attributeID', -1);
        attribute.set('attributeGuid', MPlaza.generateUUID());
        attribute.set('attributeCode', randomCode('attribute'));
        // attribute.set('attributeTypeID', MPlaza.AttributeType.Options);
        attribute.set('stepGuid', Zakeke.getEmptyUUID());
        attribute.set('groupGuid', Zakeke.getEmptyUUID());
        attribute.set('stepID', -1);
        attribute.set('groupID', -1);
        // attribute.set('constraintExpression', null);
        attribute.set('name', '');
        attribute.set('description', '');
        attribute.set('enabled', true);
        attribute.set('displayOrder', displayOrder || 0);

        return attribute;
    }
    static createNewGroup(displayOrder?: number): MPlaza.AttributeGroup {
        const group = new MPlaza.AttributeGroup();
        group.set('groupGuid', MPlaza.generateUUID());
        group.set('groupID', -1);
        group.set('name', '');
        group.set('enabled', true);
        group.set('displayOrder', displayOrder || 0);
        return group;
    }

    static createNewStep(displayOrder?: number): MPlaza.AttributeStep {
        const step = new MPlaza.AttributeStep();
        step.set('stepGuid', MPlaza.generateUUID());
        step.set('stepID', -1);
        step.set('name', '');
        step.set('enabled', true);
        step.set('displayOrder', displayOrder || 0);
        return step;
    }

    static createNewOption(displayOrder?: number): MPlaza.AttributeOption {
        const option = new MPlaza.AttributeOption();
        option.set('optionGuid', MPlaza.generateUUID());
        option.set('optionID', -1);
        option.set('name', '');
        option.set('enabled', true);
        option.set('optionCode', randomCode('option'));
        option.set('displayOrder', displayOrder || 0);
        return option;
    }

    static createNewAction(): MPlaza.AttributeSceneAction {
        const action = new MPlaza.AttributeSceneAction();
        action.set('actionGuid', MPlaza.generateUUID());
        action.set('name', '');
        action.set('enabled', true);
        action.set('execOrder', 0);
        action.set('actionTypeID', GetActionValueFromType(ComposerOptionActionsType.SHOW_OBJECTS));
        action.set('sceneElementTypeID', MPlaza.SceneElementType.Mesh);
        action.set('selector', '');
        return action;
    }

    static toNode(attribute: MPlaza.Attribute): ComposerAttributeNode {
        if (!attribute) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        const attributeCode = attribute.get('attributeCode') || randomCode('attribute');
        const pricedOptionGuids = new Set<string>(
            attribute.get('pricings')?.map((pricing: any) => pricing.get?.('selectedOptionGuid') ?? pricing.selectedOptionGuid ?? '') || []
        );
        return {
            id: attribute.get('attributeGuid'),
            idNumeric: attribute.get('attributeID') || -1,
            code: attributeCode,
            description: attribute.get('description') || '',
            enabled: attribute.get('enabled') !== false,
            name: attribute.get('name') || T._('Attribute', 'Admin'),
            displayOrder: attribute.get('displayOrder') || 0,
            type: ComposerAttributeType.Attribute,
            objects: attribute.get('meshSelector') || '',
            camera: attribute.get('sceneCameraLocationID') || '',
            parentID: getParentGuid(attribute.get('stepGuid'), attribute.get('groupGuid')),
            parentIDNumeric: getParentIDNumeric(attribute.get('stepID'), attribute.get('groupID')),
            parentType: getParentType(attribute.get('stepGuid'), attribute.get('groupGuid')),
            options:
                attribute
                    .get('options')
                    ?.map((option) =>
                        ComposerAttributeNodeAdapter.toNodeOption(attribute.get('attributeGuid'), option, pricedOptionGuids.has(option.get('optionGuid')))
                    ) || [],
            pricings:
                attribute.get('pricings')?.map((p: any) => ({
                    attributePricingID: p.get?.('attributePricingID') ?? p.attributePricingID ?? 0,
                    selectedOptionID: p.get?.('selectedOptionID') ?? p.selectedOptionID ?? 0,
                    selectedOptionGuid: p.get?.('selectedOptionGuid') ?? p.selectedOptionGuid ?? '',
                    selectedValue: p.get?.('selectedValue') ?? p.selectedValue ?? '',
                    price: p.get?.('price') ?? p.price ?? 0,
                    pricingID: p.get?.('pricingID') ?? p.pricingID ?? 0,
                    name: p.get?.('name') ?? p.name ?? ''
                })) || [],
            platformAttribute: attributeCode.startsWith('{') ? tryParsePlatformValue(attributeCode) : undefined,
            previewType: (attribute.get('optionPreviewShapeID') as unknown as ComposerAttributePreviewType) || ComposerAttributePreviewType.Rectangle,
            hideOptionsLabel: attribute.get('hideOptionsLabel') || false,
            linkOperation: (attribute.get('constraintExpression')?.get('op') as unknown as ComposerLogicalOperator) || ComposerLogicalOperator.AND,
            links:
                attribute
                    .get('constraintExpression')
                    ?.get('propositions')
                    ?.map((link: any) => ({
                        attributeID: link.attributeGuid || '',
                        optionID: link.value || '',
                        operation: link.op || ComposerComparisonOperator.EQUAL
                    })) || [],
            brokenValues: new Map(),
            data: attribute.toJSON()
        };
    }

    static toNodeActionFromAiAttribute(attributeID: string, optionID: string, action: AttributePreviewAction): ComposerOptionActionNode {
        const nodeAction = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        nodeAction.type = GetActionTypeFromAiType(action.type);
        nodeAction.selector = action.objects.join(';');
        nodeAction.value = action.material;
        return nodeAction;
    }

    static toNodeOptionFromAiAttribute(attributeID: string, option: AttributePreviewOption): ComposerAttributeOptionNode {
        const nodeOption = ComposerAttributeNodeAdapter.toNodeOption(attributeID, ComposerAttributeNodeAdapter.createNewOption());
        nodeOption.name = option.name;
        nodeOption.actions = option.actions.map((action) => ComposerAttributeNodeAdapter.toNodeActionFromAiAttribute(attributeID, nodeOption.id, action));
        return nodeOption;
    }

    static toNodeFromAiAttribute(attribute: AttributePreviewItems): ComposerAttributeNode {
        const nodeAttribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        nodeAttribute.name = attribute.name;
        nodeAttribute.options = attribute.options.map((option) => ComposerAttributeNodeAdapter.toNodeOptionFromAiAttribute(nodeAttribute.id, option));
        return nodeAttribute;
    }

    static toNodeOption(attributeID: string, option: MPlaza.AttributeOption, preserveEmptyCode = false): ComposerAttributeOptionNode {
        if (!option) {
            return ComposerAttributeNodeAdapter.toNodeOption(attributeID, ComposerAttributeNodeAdapter.createNewOption());
        }
        return {
            id: option.get('optionGuid'),
            idNumeric: option.get('optionID') || -1,
            code: preserveEmptyCode ? option.get('optionCode') || '' : option.get('optionCode') || randomCode('option'),
            name: option.get('name') || T._('Option', 'Admin'),
            description: option.get('description') || '',
            enabled: option.get('enabled') !== false,
            displayOrder: option.get('displayOrder') || 0,
            type: ComposerAttributeType.Attribute,
            img: option.get('imageUrl') || '',
            attributeID: attributeID,
            isDefault: option.get('isDefault') || false,
            platformAttribute: tryParsePlatformValue(option.get('optionCode')),
            linkOperation: (option.get('constraintExpression')?.get('op') as unknown as ComposerLogicalOperator) || ComposerLogicalOperator.AND,
            links:
                option
                    .get('constraintExpression')
                    ?.get('propositions')
                    ?.map((link: any) => ({
                        attributeID: link.attributeGuid || '',
                        optionID: link.value || '',
                        operation: link.op || ComposerComparisonOperator.EQUAL
                    })) || [],
            actions: option.get('actions')?.map((action) => ComposerAttributeNodeAdapter.toNodeAction(attributeID, option.get('optionGuid'), action)) || [],
            extensionFields:
                (option as any).get('extensionFields')?.map((ef: any) => ({
                    extensionFieldID: ef.get?.('extensionFieldID') ?? ef.extensionFieldID ?? 0,
                    name: ef.get?.('name') ?? ef.name ?? '',
                    label: ef.get?.('label') ?? ef.label ?? '',
                    value: ef.get?.('value') ?? ef.value ?? '',
                    dataTypeID: ef.get?.('dataTypeID') ?? ef.dataTypeID ?? 0,
                    operationTypeID: ef.get?.('operationTypeID') ?? ef.operationTypeID ?? 0,
                    formatString: ef.get?.('formatString') ?? ef.formatString ?? ''
                })) || [],
            brokenValues: new Map(),
            data: option.toJSON()
        } as ComposerAttributeOptionNode;
    }

    static toNodeAction(attributeID: string, optionID: string, action: MPlaza.AttributeSceneAction): ComposerOptionActionNode {
        if (!action) {
            return ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        }
        return {
            id: action.get('actionGuid'),
            name: action.get('name') || T._('Action', 'Admin'),
            enabled: action.get('enabled') !== false,
            type: GetActionTypeFromValue(action.get('actionTypeID'), action.get('value') ?? ''),
            sceneElementType: action.get('sceneElementTypeID') ?? MPlaza.SceneElementType.Mesh,
            displayOrder: action.get('execOrder') || 0,
            optionID: optionID,
            attributeID: attributeID,
            selector: action.get('selector') || '',
            property: action.get('propertyName') || '',
            value: action.get('value') || '',
            brokenValues: new Map(),
            data: action.toJSON()
        } as ComposerOptionActionNode;
    }

    static toStepNode(step: MPlaza.AttributeStep): ComposerAttributeNode {
        if (!step) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        const groupGuid = step.get('groupGuid');
        return {
            id: step.get('stepGuid'),
            idNumeric: step.get('stepID') || -1,
            code: step.get('stepGuid') || '',
            description: '',
            name: step.get('name') || T._('Step', 'Admin'),
            enabled: step.get('enabled') !== false,
            displayOrder: step.get('displayOrder') || 0,
            type: ComposerAttributeType.AttributeStep,
            parentID: guidIsEmpty(groupGuid) ? undefined : groupGuid || undefined,
            parentIDNumeric: step.get('groupID') || -1,
            parentType: guidIsEmpty(groupGuid) ? undefined : ComposerAttributeType.AttributeGroup,
            camera: (step as any).get('cameraLocationID') || undefined,
            options: [],
            pricings: [],
            links: [],
            brokenValues: new Map(),
            data: step.toJSON()
        };
    }

    static toGroupNode(group: MPlaza.AttributeGroup): ComposerAttributeNode {
        if (!group) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        return {
            id: group.get('groupGuid'),
            idNumeric: group.get('groupID') || -1,
            code: group.get('groupGuid') || '',
            name: group.get('name') || T._('Group', 'Admin'),
            enabled: group.get('enabled') !== false,
            description: '',
            displayOrder: group.get('displayOrder') || 0,
            type: ComposerAttributeType.AttributeGroup,
            img: group.get('imageUrl') || '',
            parentID: undefined,
            parentIDNumeric: -1,
            camera: group.get('sceneCameraLocationID') || undefined,
            objects: group.get('meshSelector') || undefined,
            direction: group.get('direction') ?? 0,
            attributesAlwaysOpened: group.get('attributesAlwaysOpened') || false,
            options: [],
            pricings: [],
            links: [],
            brokenValues: new Map(),
            data: group.toJSON()
        };
    }

    static adjustValue(attribute: ComposerAttributeNode): ComposerAttributeNode {
        const adjustedAttribute = { ...attribute };
        if (adjustedAttribute.options && adjustedAttribute.options.every((o) => !o.isDefault)) {
            adjustedAttribute.options = adjustedAttribute.options.map((option, index) => {
                return {
                    ...option,
                    isDefault: index === 0 // Set the first option as default
                };
            });
        }
        return adjustedAttribute;
    }

    static adjustValues(attributes: ComposerAttributeNode[]): ComposerAttributeNode[] {
        return attributes.map(ComposerAttributeNodeAdapter.adjustValue);
    }
}

export class ComposerTemplateNodeAdapter {
    static toNode(id: number, name: string, design: any, nodeID: string | null = null, model: MPlaza.Model, sides: MPlaza.Sides): ComposerTemplateNode {
        const designObj: MPlaza.Design = design
            ? new MPlaza.Design(JSON.parse(JSON.stringify(design)))
            : ComposerTemplateNodeAdapter.createNewDesign(name, model, sides);
        const designSides = designObj.get('sides');
        const restrictions = new Map();
        for (const restriction of designSides.models) {
            const uploadRestrictions = restriction?.get('uploadRestrictions');
            const objRestriction: ComposerTemplateSideRestrictions = {
                userCanAddTexts: restrictions?.get('canAddText') || false,
                userCanAddImages: restrictions?.get('canAddImage') || false,
                isGalleryEnabled: !restrictions?.get('disableSellerImages'),
                userCanAddUploadImages: uploadRestrictions?.isUserImageAllowed || false,
                jpgUploadEnabled: uploadRestrictions?.isJpgAllowed || false,
                pngUploadEnabled: uploadRestrictions?.isPngAllowed || false,
                svgUploadEnabled: uploadRestrictions?.isSvgAllowed || false,
                pdfUploadEnabled: uploadRestrictions?.isPdfAllowed || false,
                pdfWithRasterizationEnabled: uploadRestrictions?.isPdfWithRasterAllowed || false,
                epsUploadEnabled: uploadRestrictions?.isEpsAllowed || false
            };
            restrictions.set(restriction.get('sideID'), objRestriction);
        }
        return {
            templateID: id,
            designDocID: design?.docID || '',
            name: name,
            design: designObj,

            //restriction
            restrictions: restrictions,
            //ui
            id: nodeID ?? MPlaza.generateUUID(),
            data: null, //items?.toJSON(),
            expanded: true
        };
    }

    static fromTemplateNode(node: ComposerTemplateNode): MPlaza.Design {
        const design = new MPlaza.Design(JSON.parse(JSON.stringify(node.design)));
        const designSides = design.get('sides');
        for (const designSide of designSides.models) {
            const sideRestrictions = node.restrictions.get(designSide.get('sideID'));
            designSide?.set('canAddText', !!sideRestrictions?.userCanAddTexts);
            designSide?.set('canAddImage', !!sideRestrictions?.userCanAddImages);
            designSide?.set('disableSellerImages', !sideRestrictions?.isGalleryEnabled);
            const uploadRestrictions = designSide?.get('uploadRestrictions');

            if (uploadRestrictions) {
                uploadRestrictions.isUserImageAllowed = sideRestrictions?.userCanAddUploadImages || false;
                uploadRestrictions.isJpgAllowed = sideRestrictions?.jpgUploadEnabled || false;
                uploadRestrictions.isPngAllowed = sideRestrictions?.pngUploadEnabled || false;
                uploadRestrictions.isSvgAllowed = sideRestrictions?.svgUploadEnabled || false;
                uploadRestrictions.isPdfAllowed = sideRestrictions?.pdfUploadEnabled || false;
                uploadRestrictions.isPdfWithRasterAllowed = sideRestrictions?.pdfWithRasterizationEnabled || false;
                uploadRestrictions.isEpsAllowed = sideRestrictions?.epsUploadEnabled || false;
            }
        }
        return design;
    }

    static createNewDesign(name: string, model: MPlaza.Model, sides: MPlaza.Sides | MPlaza.Side): MPlaza.Design {
        const design = new MPlaza.Design();
        design.set('name', name);
        design.setModel(model);
        // Configurator products no longer get an auto-created default printing method, so printTypes may be empty.
        // The active PM is selected per attribute option via the SetPrintingMethod scene action instead.
        const defaultPrintType = model.get('printTypes')?.at(0);
        if (defaultPrintType) design.setPrintType(defaultPrintType);
        design.set('sides', new MPlaza.DesignSides());
        if (sides instanceof MPlaza.Side) {
            const designSide = new (MPlaza.DesignSide as any)({
                modelID: model.get('modelID'),
                colorID: model.get('colors').at(0).get('colorID'),
                sideID: sides.get('sideID'),
                canAddText: true,
                canAddImage: true,
                disableSellerImages: false,
                maxNrTexts: null,
                maxNrImages: null,
                uploadRestrictions: {
                    isSvgAllowed: true,
                    isPngAllowed: true,
                    isJpgAllowed: true
                }
            });
            design.get('sides').add(designSide);
        } else {
            for (const side of sides.models) {
                const designSide = new (MPlaza.DesignSide as any)({
                    modelID: model.get('modelID'),
                    colorID: model.get('colors').at(0).get('colorID'),
                    sideID: side.get('sideID'),
                    canAddText: true,
                    canAddImage: true,
                    disableSellerImages: false,
                    maxNrTexts: null,
                    maxNrImages: null,
                    uploadRestrictions: {
                        isSvgAllowed: true,
                        isPngAllowed: true,
                        isJpgAllowed: true
                    }
                });
                design.get('sides').add(designSide);
            }
        }
        design.tempID = Zakeke.generateUUID();
        return design;
    }

    static createNewTemplate(name: string, model: MPlaza.Model, side: MPlaza.Side): ComposerTemplateNode {
        return {
            templateID: 0,
            designDocID: '',
            name: name,
            design: ComposerTemplateNodeAdapter.createNewDesign(name, model, side),

            //restriction
            restrictions: new Map(),
            //ui
            id: MPlaza.generateUUID(),
            data: null, //items?.toJSON(),
            expanded: true
        };
    }

    static toNodeArea(side: MPlaza.Side, meshSides: MPlaza.ModelSceneMeshSides): ComposerAreaNode {
        const assignedMeshSides = meshSides.filter((mesh) => mesh.get('sideID') === side.get('sideID'));
        const meshSide = assignedMeshSides.at(0);
        const desktopTextureResolution = meshSide?.get('config')?.get('general')?.get('desktopTextureResolutionWidth') || undefined;
        const mobileTextureResolution = meshSide?.get('config')?.get('general')?.get('mobileTextureResolutionWidth') || undefined;
        return {
            name: side.get('name') || '',
            sideID: side.get('sideID') || 0,
            ppcm: side.get('ppcm') || 1,
            width: side.get('areas').at(0).get('boundsWidth') || 0,
            height: side.get('areas').at(0).get('boundsHeight') || 0,
            //effect: presente commentato nel codice ma forse deprecato?
            camera: (side as any).get('cameraLocationID') || undefined,
            uvMap: meshSide?.get('uvChannel') || 0,
            customTextureResolution: !!desktopTextureResolution || !!mobileTextureResolution,
            desktopTextureResolution: desktopTextureResolution,
            mobileTextureResolution: mobileTextureResolution,
            originalSide: side ?? new MPlaza.Side(),
            originalMeshSides: assignedMeshSides.map((item) => item),
            objects:
                meshSides
                    ?.filter((mesh) => mesh.get('sideID') === side.get('sideID'))
                    .map((mesh) => mesh.get('meshID'))
                    .join(';') || '',

            //ui
            id: (side as any).get('sideGuid') || (side as any).get('id') || MPlaza.generateUUID(),
            data: {
                side: side.toJSON(),
                meshSides: assignedMeshSides.map((item) => item.toJSON())
            },
            templateID: ''
        };
    }

    static fromAreaNode(node: ComposerAreaNode | null): { side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides } {
        const result: { side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides } = {
            side: node?.originalSide || new MPlaza.Side(),
            modelSides: new MPlaza.ModelSceneMeshSides()
        };
        console.log('FROM AREA NODE', node);
        const sideGuid = (result.side as any).get('sideGuid') || node?.id || MPlaza.generateUUID();
        const sideID = node?.sideID && node.sideID > 0 ? node.sideID : ComposerAttributeNodeAdapter.temporaryNumericID(sideGuid);
        (result.side as any).set('id', sideGuid);
        result.side.set('sideID', sideID);
        (result.side as any).set('sideGuid', sideGuid);
        result.side.set('name', node?.name || '');
        result.side.set('ppcm', node?.ppcm || 1024 / (node?.width || 1));
        if (result.side.get('areas').length > 0) {
            const area = result.side.get('areas').at(0);
            area.set('guid', area.get('guid') || MPlaza.generateUUID());
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
        } else {
            const areas = new MPlaza.Areas();
            const area = new MPlaza.Area();
            area.set('areaID', 0);
            area.set('guid', MPlaza.generateUUID());
            area.set('name', node?.name || '');
            area.set('path', '');
            area.set('pathSVG', '');
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
            areas.add(area);
            result.side.set('areas', areas);
        }
        const selectedMeshIDs = new Set((node?.objects.split(';') || []).filter(Boolean));
        const persistedMeshSides = node?.originalMeshSides ?? [];
        for (const meshID of selectedMeshIDs) {
            const matchingMeshSides = persistedMeshSides.filter((item) => item.get('meshID') === meshID);
            const sourceMeshSides: Array<MPlaza.ModelSceneMeshSide | null> = matchingMeshSides.length > 0 ? matchingMeshSides : [null];
            for (const sourceMeshSide of sourceMeshSides) {
                const mesh = sourceMeshSide ?? new MPlaza.ModelSceneMeshSide();
                mesh.set('meshID', meshID);
                mesh.set('sideID', result.side.get('sideID'));
                mesh.set('uvChannel', node?.uvMap ?? mesh.get('uvChannel') ?? 0);
                const generalConfig = mesh.getOrCreateConfig().getGeneralConfig();
                (generalConfig as any)?.set(
                    'desktopTextureResolutionWidth',
                    node?.desktopTextureResolution ?? (generalConfig as any).get('desktopTextureResolutionWidth')
                );
                (generalConfig as any)?.set(
                    'desktopTextureResolutionHeight',
                    node?.desktopTextureResolution ?? (generalConfig as any).get('desktopTextureResolutionHeight')
                );
                (generalConfig as any)?.set(
                    'mobileTextureResolutionWidth',
                    node?.mobileTextureResolution ?? (generalConfig as any).get('mobileTextureResolutionWidth')
                );
                (generalConfig as any)?.set(
                    'mobileTextureResolutionHeight',
                    node?.mobileTextureResolution ?? (generalConfig as any).get('mobileTextureResolutionHeight')
                );
                mesh.getConfig()?.set('general', generalConfig);
                result.modelSides.add(mesh);
            }
        }
        return result;
    }

    static fromAreaNodeToSide(node: ComposerAreaNode | null): MPlaza.Side {
        const side = node?.originalSide || new MPlaza.Side();
        (side as any).set('id', node?.id || MPlaza.generateUUID());
        side.set('sideID', node?.sideID || 0);
        side.set('name', node?.name || '');
        side.set('ppcm', node?.ppcm || 1024 / (node?.width || 1));
        if (side.get('areas').length > 0) {
            const area = side.get('areas').at(0);
            area.set('guid', area.get('guid') || MPlaza.generateUUID());
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
        } else {
            const areas = new MPlaza.Areas();
            const area = new MPlaza.Area();
            area.set('areaID', 0);
            area.set('guid', MPlaza.generateUUID());
            area.set('name', node?.name || '');
            area.set('path', '');
            area.set('pathSVG', '');
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
            areas.add(area);
            side.set('areas', areas);
        }
        return side;
    }

    static saveTemplates(
        templates: ComposerTemplateNode[],
        areas: ComposerAreaNode[]
    ): {
        designs: MPlaza.Designs;
        sides: MPlaza.Sides;
        modelSceneMeshSides: MPlaza.ModelSceneMeshSides;
    } {
        const resultDesigns = new MPlaza.Designs();
        const resultSides = new MPlaza.Sides();
        const resultModelSceneMeshSides = new MPlaza.ModelSceneMeshSides();
        templates.forEach((template) => {
            resultDesigns.push(ComposerTemplateNodeAdapter.fromTemplateNode(template));
        });
        areas.forEach((area) => {
            const { side, modelSides } = ComposerTemplateNodeAdapter.fromAreaNode(area);
            resultSides.push(side);
            modelSides.forEach((meshSide) => {
                resultModelSceneMeshSides.push(meshSide);
            });
        });
        return { designs: resultDesigns, sides: resultSides, modelSceneMeshSides: resultModelSceneMeshSides };
    }
}
